import React, { useEffect, useState } from 'react';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { QuickSessionAccess } from './QuickSessionAccess';
import { CreditWarningBanner } from './CreditWarningBanner';
import { GlobalJobNotificationBanner } from './GlobalJobNotificationBanner';
import MeditativeBackground from './MeditativeBackground';
import { ThemeToggle } from './ThemeToggle';
import ZenAnimationWrapper from './ZenAnimationWrapper';
import SearchBar from './SearchBar';
import { useScrollAnimations } from '@/hooks/useScrollAnimation';
import { useCreditWarnings } from '@/hooks/useCreditWarnings';
import { GiftNotificationWatcher } from '@/components/GiftReceivedToast';
import { Menu } from 'lucide-react';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  useScrollAnimations();
  const [quickAccessOpen, setQuickAccessOpen] = useState(false);

  // Initialize credit warning system (handles toast notifications)
  useCreditWarnings();

  // Keyboard shortcut for Cmd+K (Mac) or Ctrl+K (Windows)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setQuickAccessOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <SidebarProvider defaultOpen={true}>
      {/* Watch for new gift notifications */}
      <GiftNotificationWatcher />

      <div className="min-h-screen flex w-full">
        <MeditativeBackground />

        <AppSidebar />

        <main className="flex-1 relative main-content z-10">
          <header className="sticky top-0 z-50 flex items-center h-16 px-4 bg-background/95 backdrop-blur-md border-b border-border/50 zen-nav shadow-zen">
            <SidebarTrigger className="p-2 rounded-md zen-button zen-ripple cursor-pointer relative z-10" />

            <div className="flex-1 flex items-center justify-center max-w-md mx-4">
              <ZenAnimationWrapper animation="slide" delay={200}>
                <SearchBar
                  placeholder="Search wellness sessions..."
                  compact
                  className="w-full"
                />
              </ZenAnimationWrapper>
            </div>

            <div className="flex items-center gap-3">
              <ZenAnimationWrapper animation="fade" delay={400}>
                <ThemeToggle />
              </ZenAnimationWrapper>
            </div>
          </header>

          <div className="p-6">
            {/* Job Status Banner - shows active/completed/failed session generations */}
            <GlobalJobNotificationBanner />

            {/* Credit Warning Banner - shows at top of content when credits are low */}
            <CreditWarningBanner />

            <ZenAnimationWrapper animation="fade" className="scroll-fade">
              {children}
            </ZenAnimationWrapper>
          </div>
        </main>
      </div>

      {/* Quick Session Access Modal */}
      <QuickSessionAccess open={quickAccessOpen} onOpenChange={setQuickAccessOpen} />
    </SidebarProvider>
  );
}